import { Box, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { ACCENTS, COLORS, fmtDuration } from "./emsTheme";
import { SEVERITY_META } from "./severity";

// Shared look for the Tickets list, the ticket detail page and their modals. Colours come from
// emsTheme's ACCENTS so the Tickets pages read the same as Analytics.

/** Ticket status, by its statusCategory: open needs action (orange), pending is waiting on
 *  someone (blue), resolved is done (green), closed is out of the way (grey). */
export const STATUS_CATEGORY_META = {
    OPEN: { solid: ACCENTS.orange.solid, soft: ACCENTS.orange.soft, border: "#F7CDB5" },
    PENDING: { solid: ACCENTS.blue.solid, soft: ACCENTS.blue.soft, border: "#C7DCF5" },
    RESOLVED: { solid: ACCENTS.green.solid, soft: ACCENTS.green.soft, border: "#BDE8D6" },
    CLOSED: { solid: COLORS.ink2, soft: "#F3F4F6", border: COLORS.grid },
};
export const statusMeta = (category) => STATUS_CATEGORY_META[category] || STATUS_CATEGORY_META.PENDING;

/** P0 red, P1 orange, P2 purple. Labels stay the ones in severity.js. */
export const SEVERITY_TONE = {
    P0: { solid: ACCENTS.red.solid, soft: ACCENTS.red.soft, border: "#F5C2C2" },
    P1: { solid: ACCENTS.orange.solid, soft: ACCENTS.orange.soft, border: "#F7CDB5" },
    P2: { solid: ACCENTS.purple.solid, soft: ACCENTS.purple.soft, border: "#DCCDEE" },
};

// Internal-only sub-statuses (the backend never sends subStatus to a merchant-scoped caller).
export const SUB_STATUS_META = {
    REOPENED: { label: "Reopened", solid: ACCENTS.purple.solid, soft: ACCENTS.purple.soft, border: "#DCCDEE" },
    ON_HOLD: { label: "On Hold", solid: ACCENTS.blue.solid, soft: ACCENTS.blue.soft, border: "#C7DCF5" },
    DELAYED: { label: "Delayed", solid: ACCENTS.red.solid, soft: ACCENTS.red.soft, border: "#F5C2C2" },
};

const pillSx = (tone, height = 24) => ({
    display: "inline-flex", alignItems: "center", gap: 0.625, height, px: 1.125, borderRadius: "999px", maxWidth: "100%",
    bgcolor: tone.soft, color: tone.solid, border: `1px solid ${tone.border}`, fontSize: 12, fontWeight: 700, lineHeight: 1,
    whiteSpace: "nowrap", flexShrink: 0,
});
const Dot = ({ color }) => <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />;

export const StatusPill = ({ status, category, height }) => {
    const tone = statusMeta(category);
    return (
        <Box component="span" sx={pillSx(tone, height)} title={status}>
            <Dot color={tone.solid} />
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{status || "—"}</Box>
        </Box>
    );
};

export const SubStatusPill = ({ subStatus, height }) => {
    const meta = SUB_STATUS_META[subStatus];
    if (!meta) return null;
    return <Box component="span" sx={pillSx(meta, height)}>{meta.label}</Box>;
};

/** "P0" in the table; "P0 — Critical" with long. */
export const SeverityPill = ({ severity, long, height }) => {
    const tone = SEVERITY_TONE[severity];
    if (!tone) return null;
    return (
        <Box component="span" sx={pillSx(tone, height)} title={SEVERITY_META[severity]?.label}>
            {long ? SEVERITY_META[severity]?.label || severity : severity}
        </Box>
    );
};

/** A soft, rounded chip for category-like text, ellipsised with a title for the full value. */
export const TagPill = ({ label, tone = "purple", maxWidth = 180 }) => {
    const t = tone === "neutral" ? { solid: COLORS.ink2, soft: "#F3F4F6", border: COLORS.grid } : { ...ACCENTS[tone], border: "#DCCDEE" };
    return (
        <Box component="span" title={label} sx={{ ...pillSx(t), fontWeight: tone === "neutral" ? 600 : 700, maxWidth, display: "inline-block", lineHeight: "22px",
            overflow: "hidden", textOverflow: "ellipsis" }}>{label}</Box>
    );
};

const IST_DAY = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const OPEN_CATEGORIES = ["OPEN", "PENDING"];

/** Where an open ticket stands against its resolution TAT — the same definitions the
 *  overdueOnly / dueTodayOnly filters use (past = due before now; today = due on today's IST date). */
export const tatState = (dueAt, statusCategory, now = Date.now()) => {
    if (!dueAt || !OPEN_CATEGORIES.includes(statusCategory)) return null;
    const due = new Date(dueAt);
    if (Number.isNaN(due.getTime())) return null;
    const diffMin = (due.getTime() - now) / 60000;
    if (diffMin < 0) return { kind: "past", minutes: -diffMin };
    if (IST_DAY(due) === IST_DAY(new Date(now))) return { kind: "today", minutes: diffMin };
    return { kind: "later", minutes: diffMin };
};

/** Small badge for an open ticket's TAT: "Past TAT · 5h" or "Due in 3h" (today). Nothing otherwise
 *  unless showLater, which adds a neutral "Due in 2d". */
export const TatBadge = ({ dueAt, statusCategory, now, showLater = false, height = 22 }) => {
    const s = tatState(dueAt, statusCategory, now);
    if (!s || (s.kind === "later" && !showLater)) return null;
    const tone = s.kind === "past" ? { ...ACCENTS.red, border: "#F5C2C2" }
        : s.kind === "today" ? { ...ACCENTS.orange, border: "#F7CDB5" }
            : { solid: COLORS.ink2, soft: "#F3F4F6", border: COLORS.grid };
    const Icon = s.kind === "past" ? ErrorOutlineIcon : ScheduleIcon;
    const text = s.kind === "past" ? `Past TAT · ${fmtDuration(s.minutes)}` : `Due in ${fmtDuration(s.minutes)}`;
    return (
        <Box component="span" sx={{ ...pillSx(tone, height), px: 0.875 }}>
            <Icon sx={{ fontSize: 14 }} aria-hidden />{text}
        </Box>
    );
};

/** Purple outlined pill button for secondary actions. */
export const pillBtnSx = {
    textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: "999px", px: 1.5, color: COLORS.purple, borderColor: "#D9CCEA", bgcolor: "#fff",
    "&:hover": { borderColor: COLORS.purple, bgcolor: COLORS.purpleSoft },
};

/** Round icon button tinted with an accent. */
export const softIconBtnSx = (accent) => ({
    width: 30, height: 30, color: ACCENTS[accent].solid, bgcolor: ACCENTS[accent].soft,
    transition: "background-color 150ms, transform 150ms",
    "&:hover": { bgcolor: ACCENTS[accent].soft, filter: "brightness(.95)", transform: "translateY(-1px)" },
    "&.Mui-disabled": { bgcolor: "#F3F4F6", color: "#C4C4CC" },
    "@media (prefers-reduced-motion: reduce)": { "&:hover": { transform: "none" } },
});

/** Rounded dialog paper, shared by every Tickets modal. */
export const DIALOG_PAPER_SX = { borderRadius: "14px", boxShadow: "0 24px 64px rgba(46,27,71,.22)" };

/** Dialog title row: gradient icon tile, title, optional one-line purpose, close button. */
export const TicketsDialogTitle = ({ icon: Icon, title, sub, onClose, closeDisabled, accent = "purple", sx }) => (
    <DialogTitle component="div" sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 1.5, pb: 1.5, ...sx }}>
        {Icon && (
            <Box aria-hidden sx={{ width: 36, height: 36, borderRadius: "10px", background: ACCENTS[accent].grad, display: "grid", placeItems: "center", flexShrink: 0,
                boxShadow: `0 4px 10px ${ACCENTS[accent].solid}40` }}>
                <Icon sx={{ fontSize: 20, color: "#fff" }} />
            </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: 17, fontWeight: 800, color: COLORS.ink, lineHeight: 1.3 }}>{title}</Typography>
            {sub && <Typography sx={{ fontSize: 12, color: COLORS.ink3 }}>{sub}</Typography>}
        </Box>
        {onClose && (
            <IconButton onClick={onClose} size="small" disabled={closeDisabled} aria-label="Close"
                sx={{ color: COLORS.ink3, "&:hover": { bgcolor: COLORS.purpleSoft, color: COLORS.purple } }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        )}
    </DialogTitle>
);
